<i18n lang="yaml">
  pt:
    apply: 'Candidate-se'
  en:
    apply: 'Apply'
</i18n>

<template>
  <v-app class="public-layout">
    <PublicTopbar v-if="!routesWithoutTopbar && !inIframe" />

    <v-main class="public-layout__wrapper">
      <NuxtPage />
      <deck-button
        v-if="showApplyButton"
        class="public-layout__apply-button"
        size="large"
        :text="t('apply')"
        @click="onApplyButtonClick"
      />
    </v-main>

    <Snackbar v-if="!inIframe" />
    <TheDialog />
  </v-app>
</template>

<script lang="ts">
import RedirectToView from '~/assets/javascript/modules/member/actions/redirect-to-view';
import Snackbar from '~/components/Snackbar';
import TheDialog from '~/components/dialogs/the-dialog';
import PublicTopbar from '~/components/topbars/public-topbar';
import { mapMutations, mapState } from '~/assets/javascript/modules/vuex';
import ROUTES from '~/assets/javascript/constants/routes';

const routesWithoutTopbar = [
  't-tenantSlug-login',
  't-tenantSlug-sign-up',
  't-tenantSlug-reset-password',
];

export default defineComponent({
  name: 'Public',
  components: {
    Snackbar,
    TheDialog,
    PublicTopbar,
  },
  setup() {
    const route = useRoute();
    const $getRouteBaseName = useRouteBaseName();
    const baseRouteName = computed(() => $getRouteBaseName(route));

    useWorkspace({ isPublic: true });
    buildHead();

    return {
      baseRouteName,
      t: useI18n().t,
      ...mapState('workspace', ['workspace']),
      ...mapState('view', ['view']),
      ...mapMutations('environment', ['setCurrentEnvironment']),
      ...mapMutations('studio', ['setOutsideStudio']),
      ...mapState('environment', ['inIframe']),
    };
  },
  computed: {
    pageStateComponent() {
      return this.view.page_state_components?.single_item?.main_section || {};
    },
    primaryBtn() {
      return this.pageStateComponent.primary_button || {};
    },
    routesWithoutTopbar() {
      return routesWithoutTopbar.includes(this.baseRouteName);
    },
    isPublicView() {
      return this.$route.name?.includes(ROUTES.public.view.routeName);
    },
    isPublicRecord() {
      return this.$route.name?.includes(ROUTES.public.view.record.routeName);
    },
    isPublicViewOrRecord() {
      return this.isPublicView || this.isPublicRecord;
    },
    showApplyButton() {
      return this.isPublicViewOrRecord
        && !this.$auth.loggedIn
        && this.$vuetify.display.smAndDown
        && this.workspace.signup_enabled
        && !this.inIframe;
    },
  },
  beforeMount() {
    this.setOutsideStudio();
    this.setCurrentEnvironment('public');
    const utmsObject = _pickBy(this.$route.query, (_value, key) => _startsWith(key, 'utm_'));

    if (!_isEmpty(utmsObject)) {
      this.$analytics.track('referredFrom', utmsObject);
    }
  },
  methods: {
    onApplyButtonClick() {
      if (this.isPublicRecord && this.primaryBtn.action === 'redirect_to_view') {
        const action = new RedirectToView(this);

        action.execute({
          ...this.primaryBtn.params,
          viewId: this.primaryBtn.params?.view_id,
          recordId: this.$route.params.recordId,
        });

        return;
      }

      this.$router.push(this.$localePath({
        name: 't-tenantSlug-sign-up',
        params: {
          tenantSlug: this.$route.params.tenantSlug,
        },
      }));
    },
  },
});
</script>
<style lang="scss">
.public-layout {
  --z-the-landscape-max-width: 1280px;

  // Needs to be redeclared the same as :root in order replace the overriden var current value there
  // https://stackoverflow.com/a/63836563
  --z-the-landscape-to-viewport-distance: calc((100vw - var(--z-the-landscape-max-width)) / 2);

  --z-the-landscape-inline-spacing: max(
    var(--z-the-landscape-to-viewport-distance),
    var(--z-the-landscape-outer-spacing)
  );
}

.public-layout__apply-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 200;
  margin: 16px auto;
  width: 100%;
  max-width: fit-content;
}
</style>
